import type { Services } from '@/infrastructure/services';
import type { components } from '@/generated/contracts/api-v1';
import { v1, toResult } from '@/infrastructure/api/v1/client';
import type { ApiProblem, PublishProblemError } from '@/infrastructure/api/v1/problem';
import type { ChosenVisibility } from '@/infrastructure/api/v1/mappers/stories';
import { reconcileStoryAttributionsForScene } from '@/modules/library/utils/storyAttributionUtils';
import type { ValidationError } from '@/utils/publishValidation';
import type { PublishDevice } from './types';

type ListingInput = components['schemas']['ReviewListingInput'];
type SubmittedVersion = components['schemas']['SubmittedVersionDto'];

export interface ListingFields {
  creatorName?: string;
  projectName?: string;
  projectDescription?: string;
  projectTags?: string[];
}

export interface StoryReviewInput {
  storyId: string;
  sceneId: string;
  devices: PublishDevice[];
  visibility?: ChosenVisibility;
  name?: string;
  introduction?: string;
  address?: string;
  latitude?: string;
  longitude?: string;
  city?: string;
  listing: ListingFields;
}

export interface ProjectReviewInput {
  projectId: string;
  devices: PublishDevice[];
  visibility?: ChosenVisibility;
  listing: ListingFields;
  stories: { storyId: string; sceneId: string; name?: string; introduction?: string }[];
}

/** The server refused the submission and listed what is missing; nothing was written. */
export class PublishIncompleteError extends Error {
  constructor(readonly errors: PublishProblemError[]) {
    super('The story is not ready for review');
    this.name = 'PublishIncompleteError';
  }
}

const FIELD_NAMES: Record<string, string> = {
  'project.sourceName': 'project_source_name',
  'project.displayName': 'project_name',
  'project.description': 'project_description',
  'project.tags': 'project_tags',
  'images.projectPortrait': 'project_portrait_image',
  'images.projectLandscape': 'project_landscape_image',
  name: 'scene_name',
  introduction: 'scene_description',
  'location.address': 'scene_location',
  'images.sceneLandscape': 'scene_cover_image',
  'images.scanImage': 'scene_scan_image',
  'images.imageAnchor': 'scene_anchor_image',
  anchorCategory: 'scene_anchor',
  devices: 'scene_devices',
};

// The Missing Required Information dialog's shape
export function toValidationErrors(errors: PublishProblemError[], storyNames: Record<string, string>): ValidationError[] {
  return errors.map(error => ({
    field: FIELD_NAMES[error.field] ?? error.field,
    message: error.message,
    category: error.storyUniqueId ? 'scene' : 'project',
    sceneOriginId: error.storyUniqueId ?? undefined,
    sceneName: error.storyUniqueId ? storyNames[error.storyUniqueId] : undefined,
  }));
}

function listingBody(listing: ListingFields): ListingInput | undefined {
  const body: ListingInput = {
    ...(listing.projectName !== undefined ? { displayName: listing.projectName } : {}),
    ...(listing.projectDescription !== undefined ? { description: listing.projectDescription } : {}),
    ...(listing.creatorName !== undefined ? { sourceName: listing.creatorName } : {}),
    ...(listing.projectTags !== undefined ? { tags: listing.projectTags } : {}),
  };
  return Object.keys(body).length > 0 ? body : undefined;
}

function coordinate(value: string | undefined): number | undefined {
  if (value === undefined || value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function refusal(error: ApiProblem): Error {
  if (error.code === 'publish_incomplete' && error.errors.length > 0) return new PublishIncompleteError(error.errors);
  return new Error(`This wasn't submitted for review. ${error.title}`, { cause: error });
}

// Best-effort follow-ups the server does not do: credits for the version now governing the story, and analytics
async function afterSubmit(services: Services, submitted: SubmittedVersion[], projectId?: string) {
  for (const version of submitted) {
    try {
      await reconcileStoryAttributionsForScene(version.sceneUniqueId);
    } catch (error) {
      console.warn('Failed to reconcile story attributions after submitting:', error);
    }
    services.analytics.trackLureoPublishedScene({
      scene_instance_id: version.sceneUniqueId,
      publish_type: version.versionNumber === 1 ? 'first_publish' : 're_publish',
      scene_version: version.versionNumber,
      project_id: projectId,
    });
  }
}

/** One request: the server checks the story, writes every field and moves the version to pending, or writes nothing. */
export async function submitStoryForReview(services: Services, input: StoryReviewInput, projectId?: string): Promise<SubmittedVersion> {
  const hasLocation = input.address !== undefined || input.latitude !== undefined || input.longitude !== undefined;
  const { data, error } = await toResult(v1.POST('/api/v1/stories/{storyUniqueId}/review-submissions', {
    params: { path: { storyUniqueId: input.storyId } },
    body: {
      uniqueId: crypto.randomUUID(),
      sceneUniqueId: input.sceneId,
      devices: input.devices,
      visibility: input.visibility,
      name: input.name,
      introduction: input.introduction,
      location: hasLocation
        ? { address: input.address, latitude: coordinate(input.latitude), longitude: coordinate(input.longitude), city: input.city }
        : undefined,
      project: listingBody(input.listing),
    },
  }));
  if (error) throw refusal(error);
  await afterSubmit(services, data.submitted, projectId);
  return data.submitted[0];
}

/** Every listed story or none: one transaction on the server. */
export async function submitProjectForReview(services: Services, input: ProjectReviewInput): Promise<SubmittedVersion[]> {
  const { data, error } = await toResult(v1.POST('/api/v1/projects/{projectUniqueId}/review-submissions', {
    params: { path: { projectUniqueId: input.projectId } },
    body: {
      uniqueId: crypto.randomUUID(),
      devices: input.devices,
      visibility: input.visibility,
      project: listingBody(input.listing),
      stories: input.stories.map(story => ({
        storyUniqueId: story.storyId,
        sceneUniqueId: story.sceneId,
        name: story.name,
        introduction: story.introduction,
      })),
    },
  }));
  if (error) throw refusal(error);
  await afterSubmit(services, data.submitted, input.projectId);
  return data.submitted;
}
